import React, { useState, useEffect } from 'react';
import axios from 'axios';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faChartLine, 
  faEnvelope, 
  faBlog, 
  faFolderPlus, 
  faSignOutAlt 
} from '@fortawesome/free-solid-svg-icons';
import './Admin.css';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';

const API = axios.create({
  baseURL: API_BASE_URL
});

API.interceptors.request.use((config) => {
  const token = localStorage.getItem('stemiot_admin_token');
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const Admin = () => {
  // Auth states
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [loginEmail, setLoginEmail] = useState('');
  const [loginPassword, setLoginPassword] = useState('');
  const [authError, setAuthError] = useState('');
  const [authLoading, setAuthLoading] = useState(false);

  // Dashboard Active Tab
  const [activeTab, setActiveTab] = useState('analytics');

  // Dashboard Data states
  const [messages, setMessages] = useState([]);
  const [analytics, setAnalytics] = useState([]);
  const [statusMessage, setStatusMessage] = useState('');

  // Blog Form state
  const [blogData, setBlogData] = useState({ title: '', excerpt: '', content: '', category: '', image: '' });
  
  // Project Form state
  const [projectData, setProjectData] = useState({ name: '', description: '', link: '', image: '' });

  useEffect(() => {
    const token = localStorage.getItem('stemiot_admin_token');
    if (token) {
      setIsAuthenticated(true);
      fetchDashboardData();
    }
  }, [isAuthenticated]);

  const fetchDashboardData = async () => {
    try {
      const [messagesRes, analyticsRes] = await Promise.all([
        API.get('/admin/messages').catch(() => ({ data: [] })),
        API.get('/admin/analytics').catch(() => ({ data: [] }))
      ]);
      setMessages(messagesRes.data || []);
      setAnalytics(analyticsRes.data || []);
    } catch (err) {
      console.error('Error fetching admin data', err);
    }
  };

  const handleLogin = async (e) => {
    e.preventDefault();
    setAuthLoading(true);
    setAuthError('');
    try {
      const { data } = await axios.post(`${API_BASE_URL}/auth/login`, {
        email: loginEmail,
        password: loginPassword
      });
      localStorage.setItem('stemiot_admin_token', data.token);
      localStorage.setItem('stemiot_admin_user', JSON.stringify(data));
      setIsAuthenticated(true);
    } catch (err) {
      setAuthError(err.response?.data?.message || 'Invalid admin credentials');
    } finally {
      setAuthLoading(false);
    }
  };

  const handleLogout = () => {
    localStorage.removeItem('stemiot_admin_token');
    localStorage.removeItem('stemiot_admin_user');
    setIsAuthenticated(false);
  };

  const handleFileChange = (e, targetForm) => {
    const file = e.target.files[0];
    if (!file) return;

    const reader = new FileReader();
    reader.readAsDataURL(file);
    reader.onload = (event) => {
      const img = new Image();
      img.src = event.target.result;
      img.onload = () => {
        const canvas = document.createElement('canvas');
        const MAX_WIDTH = 700;
        const scaleSize = MAX_WIDTH / img.width;
        canvas.width = MAX_WIDTH;
        canvas.height = img.height * scaleSize;
        const ctx = canvas.getContext('2d');
        ctx.drawImage(img, 0, 0, canvas.width, canvas.height);
        const compressedBase64 = canvas.toDataURL('image/jpeg', 0.7);

        if (targetForm === 'blog') {
          setBlogData({ ...blogData, image: compressedBase64 });
        } else if (targetForm === 'project') {
          setProjectData({ ...projectData, image: compressedBase64 });
        }
      };
    };
  };

  const handleBlogSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/blogs', blogData);
      setStatusMessage('✅ Blog article posted successfully!');
      setBlogData({ title: '', excerpt: '', content: '', category: '', image: '' });
      e.target.reset();
    } catch (err) {
      setStatusMessage('❌ Error posting blog article.');
    }
  };

  const handleProjectSubmit = async (e) => {
    e.preventDefault();
    try {
      await API.post('/projects', projectData);
      setStatusMessage('✅ Portfolio project published successfully!');
      setProjectData({ name: '', description: '', link: '', image: '' });
      e.target.reset();
    } catch (err) {
      setStatusMessage('❌ Error adding portfolio project.');
    }
  };

  // 1. Unauthenticated Login Gate
  if (!isAuthenticated) {
    return (
      <div className="admin-login-wrapper">
        <form onSubmit={handleLogin} className="admin-login-card">
          <div className="login-header">
            <h2>Stemiot Admin</h2>
            <p>Please enter administrative credentials to access the portal.</p>
          </div>

          {authError && <div className="admin-error-box">{authError}</div>}

          <div className="form-group">
            <label>Admin Email</label>
            <input
              type="email"
              placeholder="admin@stemiotsoftwares.com"
              value={loginEmail}
              onChange={(e) => setLoginEmail(e.target.value)}
              required
            />
          </div>

          <div className="form-group">
            <label>Password</label>
            <input
              type="password"
              placeholder="••••••••••••"
              value={loginPassword}
              onChange={(e) => setLoginPassword(e.target.value)}
              required
            />
          </div>

          <button 
            type="submit" 
            className="btn-submit-modern login-submit-btn" 
            disabled={authLoading}
          >
            {authLoading ? 'Verifying...' : 'Sign In'}
          </button>
        </form>
      </div>
    );
  }

  // 2. Full Admin Dashboard
  return (
    <div className="admin-dashboard-layout">
      {/* Left Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-header">
          <h3>Stemiot <span>Portal</span></h3>
        </div>
        <ul className="admin-sidebar-menu">
          <li 
            className={activeTab === 'analytics' ? 'active' : ''} 
            onClick={() => { setActiveTab('analytics'); setStatusMessage(''); }}
          >
            <FontAwesomeIcon icon={faChartLine} className="menu-icon" />
            <span>Monthly Analytics</span>
          </li>
          <li 
            className={activeTab === 'messages' ? 'active' : ''} 
            onClick={() => { setActiveTab('messages'); setStatusMessage(''); }}
          >
            <FontAwesomeIcon icon={faEnvelope} className="menu-icon" />
            <span>Client Messages ({messages.length})</span>
          </li>
          <li 
            className={activeTab === 'publish-portfolio' ? 'active' : ''} 
            onClick={() => { setActiveTab('publish-portfolio'); setStatusMessage(''); }}
          >
            <FontAwesomeIcon icon={faFolderPlus} className="menu-icon" />
            <span>Publish Portfolio</span>
          </li>
          <li 
            className={activeTab === 'publish-blog' ? 'active' : ''} 
            onClick={() => { setActiveTab('publish-blog'); setStatusMessage(''); }}
          >
            <FontAwesomeIcon icon={faBlog} className="menu-icon" />
            <span>Publish Blog</span>
          </li>
        </ul>

        <div className="admin-sidebar-footer">
          <button onClick={handleLogout} className="logout-btn">
            <FontAwesomeIcon icon={faSignOutAlt} /> Sign Out
          </button>
        </div>
      </aside>

      {/* Main Dynamic Content Area */}
      <main className="admin-content-area">
        {statusMessage && <div className="admin-status-bar">{statusMessage}</div>}

        {/* Tab 1: Monthly Analytics */}
        {activeTab === 'analytics' && (
          <div className="tab-pane">
            <h2>Site Traffic & Monthly Analytics</h2>
            <p className="tab-desc">Overview of monthly visitors interacting with Stemiot Softwares.</p>
            
            <div className="analytics-summary-cards">
              <div className="stat-card">
                <h4>Total Interacted Months</h4>
                <p className="stat-num">{analytics.length}</p>
              </div>
              <div className="stat-card">
                <h4>Total Inquiries Received</h4>
                <p className="stat-num">{messages.length}</p>
              </div>
            </div>

            <div className="analytics-table-wrapper">
              <h3>Monthly Breakdown</h3>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>Month / Year</th>
                    <th>Unique Visitor Hits</th>
                    <th>Status</th>
                  </tr>
                </thead>
                <tbody>
                  {analytics.length > 0 ? (
                    analytics.map((item, idx) => (
                      <tr key={idx}>
                        <td><strong>{item._id}</strong></td>
                        <td>{item.totalVisits} visits</td>
                        <td><span className="badge-active">Active</span></td>
                      </tr>
                    ))
                  ) : (
                    <tr>
                      <td colSpan="3" style={{ textAlign: 'center', padding: '24px' }}>No traffic recorded yet this month.</td>
                    </tr>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Tab 2: Client Messages / Inquiries */}
        {activeTab === 'messages' && (
          <div className="tab-pane">
            <h2>Client Messages & Inquiries</h2>
            <p className="tab-desc">Direct requests submitted via your public contact form.</p>
            <div className="messages-list">
              {messages.length > 0 ? (
                messages.map((msg, index) => (
                  <div key={msg._id || index} className="message-card">
                    <div className="message-card-header">
                      <div>
                        <h4>{msg.name}</h4>
                        <a href={`mailto:${msg.email}`}>{msg.email}</a>
                      </div>
                      <span className="message-date">{new Date(msg.createdAt).toLocaleDateString()}</span>
                    </div>
                    <div className="message-subject">
                      Subject: {msg.subject}
                    </div>
                    <div className="message-body">{msg.message}</div>
                  </div>
                ))
              ) : (
                <div className="admin-form" style={{ textAlign: 'center', padding: '32px' }}>
                  <p style={{ margin: 0, color: 'var(--text-secondary)' }}>No client inquiries received yet.</p>
                </div>
              )}
            </div>
          </div>
        )}

        {/* Tab 3: Publish Portfolio */}
        {activeTab === 'publish-portfolio' && (
          <div className="tab-pane">
            <h2>Publish Portfolio Project</h2>
            <p className="tab-desc">Add new production systems to your public showcase.</p>
            <form onSubmit={handleProjectSubmit} className="admin-form">
              <div className="form-grid-two">
                <div className="form-group">
                  <label>Project Name</label>
                  <input
                    type="text"
                    placeholder="e.g. SmartSave Micro-Fintech"
                    value={projectData.name}
                    onChange={(e) => setProjectData({ ...projectData, name: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Live Deployment Link</label>
                  <input
                    type="text"
                    placeholder="https://example.com"
                    value={projectData.link}
                    onChange={(e) => setProjectData({ ...projectData, link: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Project Cover Image</label>
                <div className="custom-file-upload">
                  <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'project')} required />
                </div>
              </div>

              <div className="form-group">
                <label>Project Overview & Key Modules</label>
                <textarea
                  rows="4"
                  placeholder="Explain architecture, stack used, and key functionalities..."
                  value={projectData.description}
                  onChange={(e) => setProjectData({ ...projectData, description: e.target.value })}
                  required
                />
              </div>

              <button type="submit" className="btn-submit-modern">Publish Project</button>
            </form>
          </div>
        )}

        {/* Tab 4: Publish Blog */}
        {activeTab === 'publish-blog' && (
          <div className="tab-pane">
            <h2>Publish Blog Article</h2>
            <p className="tab-desc">Draft technical insights, guides, and engineering news.</p>
            <form onSubmit={handleBlogSubmit} className="admin-form">
              <div className="form-grid-two">
                <div className="form-group">
                  <label>Article Title</label>
                  <input
                    type="text"
                    placeholder="e.g. Architecting Scalable MERN Systems"
                    value={blogData.title}
                    onChange={(e) => setBlogData({ ...blogData, title: e.target.value })}
                    required
                  />
                </div>
                <div className="form-group">
                  <label>Category</label>
                  <input
                    type="text"
                    placeholder="Cloud & VPS / Node.js / Payments"
                    value={blogData.category}
                    onChange={(e) => setBlogData({ ...blogData, category: e.target.value })}
                    required
                  />
                </div>
              </div>

              <div className="form-group">
                <label>Header Image</label>
                <div className="custom-file-upload">
                  <input type="file" accept="image/*" onChange={(e) => handleFileChange(e, 'blog')} required />
                </div>
              </div>

              <div className="form-group">
                <label>Summary Excerpt</label>
                <textarea
                  rows="2"
                  placeholder="Quick summary shown on blog preview cards..."
                  value={blogData.excerpt}
                  onChange={(e) => setBlogData({ ...blogData, excerpt: e.target.value })}
                  required
                />
              </div>

              <div className="form-group">
                <label>Main Body Content</label>
                <textarea
                  rows="8"
                  placeholder="Write complete article content here..."
                  value={blogData.content}
                  onChange={(e) => setBlogData({ ...blogData, content: e.target.value })}
                  required
                />
              </div>

              <button type="submit" className="btn-submit-modern">Publish Article</button>
            </form>
          </div>
        )}
      </main>
    </div>
  );
};

export default Admin;